import React from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'

import { DiscountInfo } from 'core/application/account/discountCodeInfo'
import SubscriptionFrequency from 'core/application/adminSignUp/common/subscriptionFrequency'
import { ProductIds } from 'core/application/commons/config'
import { SubscriptionFeesResponse } from 'core/application/subscriptions/getSubscriptionFees'

import useCurrentProduct from 'common/hooks/useCurrentProduct'

import { formatMoney } from '../../common/moneyFormatter'
import DiscountComponent from './DiscountComponent'

import './SubscriptionStyles.less'

interface TodaysChargesProps {
  discount: DiscountInfo | null
  fees: SubscriptionFeesResponse
}

const TodaysCharges: React.FC<TodaysChargesProps> = (props) => {
  const { t: translate } = useTranslation()
  const product = useCurrentProduct()
  const totalFee = (props.fees.todaysTotal - (props.discount ? props.discount.amount : 0)).toFixed(2)
  const companyBaseFee =
    props.fees.frequency == SubscriptionFrequency.Monthly ? props.fees.proratedMonthlyFee : props.fees.monthlyFee

  return (
    <div className="card t-admin-signup-make-payment-charges">
      <div className="inner-padding">
        <h4>{translate('admin-signup.subscription.payment.order-overview-label')}</h4>
      </div>

      <div className="border-top-bottom inner-padding">
        <div className="order-subtitle mb-16">
          <p className="inline">{translate('admin-signup.subscription.payment.items-label')}</p>
        </div>

        <Row justify={'space-between'} className={'mb-16'}>
          <Col span={18}>
            {product.productId === ProductIds.GroupHraId ? (
              <p className="inline">{translate('admin-signup.subscription.payment.setup-fee-label')}</p>
            ) : (
              <p className="inline">{translate('admin-signup.subscription.payment.one-time-setup-fee-label')}</p>
            )}
          </Col>

          <Col span={6} className={'text-right'}>
            {formatMoney(props.fees.setupFee)}
          </Col>
        </Row>

        <Row justify={'space-between'} className={'mb-16'}>
          <Col span={18}>
            {props.fees.frequency === SubscriptionFrequency.Monthly ? (
              <p className="inline">{translate('admin-signup.subscription.payment.monthly-platform-fee')}</p>
            ) : (
              <p className="inline">{translate('admin-signup.subscription.payment.annual-platform-fee')}</p>
            )}

            {props.fees.frequency === SubscriptionFrequency.Annual && (
              <div className="annual-platform-fee-hint">
                {translate('admin-signup.subscription.subscription-fees.annual-platform-fee-hint')}
              </div>
            )}
          </Col>

          <Col span={6} className={'text-right'}>
            {formatMoney(companyBaseFee)}

            {props.fees.frequency === SubscriptionFrequency.Monthly && (
              <div className="monthly-platform-fee-hint">
                {translate('admin-signup.subscription.subscription-fees.monthly-platform-fee-hint')}
              </div>
            )}
          </Col>
        </Row>

        <Row justify={'space-between'} className={'mb-16'}>
          <Col span={18}>
            <span className="gray-2">{translate('admin-signup.subscription.minimum-seat')}</span>

            {props.fees.frequency === SubscriptionFrequency.Annual ? (
              <>
                <div className="gray-6">
                  {` ( $${props.fees.employeeFee / 12} x ${props.fees.seatNumber} seats) x 12 months`}{' '}
                </div>
                {product.productId === ProductIds.Perks ? (
                  <div className="annual-platform-fee-hint mt-8">
                    {translate('admin-signup.subscription.subscription-fees.annual-seat-fee-hint')}
                  </div>
                ) : product.productId === ProductIds.QsehraId ? (
                  <div className="annual-platform-fee-qshera-hint mt-8">
                    {translate('admin-signup.subscription.subscription-fees.annual-seat-fee-qshera-hint')}
                  </div>
                ) : (
                  <div></div>
                )}
              </>
            ) : (
              <>
                <span className="gray-6"> {`$${props.fees.employeeFee} x ${props.fees.seatNumber} seats`} </span>
              </>
            )}
          </Col>

          <Col span={6} className={'text-right'}>
            {formatMoney(props.fees.totalEmployeeFee)}

            {props.fees.frequency === SubscriptionFrequency.Monthly && (
              <div className="monthly-platform-fee-hint">
                {translate('admin-signup.subscription.subscription-fees.monthly-platform-fee-hint')}
              </div>
            )}
          </Col>
        </Row>
      </div>

      {props.discount && (
        <DiscountComponent todaysTotal={props.fees.todaysTotal} discountAmount={props.discount.amount} />
      )}

      <div className="inner-padding">
        <h4 className="inline">{translate('admin-signup.subscription.payment.total-label')}</h4>
        <span className="weight-600 float-right">${totalFee}</span>
      </div>
    </div>
  )
}

export default TodaysCharges
